import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import {
  IEvaluationQuestion,
  IEvaluationQuestionOption,
} from 'models/evaluation.model'
import { EvaluationQuestionLinkTypeEnum } from 'enum/evaluation-question-link-type.enum'
import { useWindowSize } from 'hooks/react.hooks'
import { breakLinesFix } from 'helpers/others.helper'
import { sanitizeHtml } from 'helpers/sanitizeHtml'
import { mobileWidth } from 'enum/responsive-width.enum'
import CheckBoxRadioButtonInput from 'components/ui/forms/field/checkbox-radiobutton/CheckBoxRadioButtonInput'
import TextAreaInput from 'components/ui/forms/field/text-area/TextArea'
import { EvaluationStatesEnum } from 'enum/evaluation-states.enum'
import { IUserAnswer } from '../../Evaluation'
import styles from './EvaluationQuestion.module.scss'

interface IQuestion {
  data: IEvaluationQuestion
  number: number
  canEdit: boolean
  value: IUserAnswer
  onChange: any
  state: number
}

const EvaluationQuestion: React.FC<IQuestion> = ({
  data,
  number,
  canEdit,
  onChange,
  value,
  state,
}) => {
  const [onMobile, setOnMobile] = useState(false)
  const { t } = useTranslation()
  const windowsSize = useWindowSize()
  useEffect(() => {
    if (windowsSize.width) setOnMobile(windowsSize.width < mobileWidth)
  }, [windowsSize])

  const prepareLink = (url: string, type: number) => {
    switch (type) {
      case EvaluationQuestionLinkTypeEnum.Video:
        return (
          <video
            controls
            src={url}
            width="100%"
            height={onMobile ? 'auto' : 465}
          />
        )
      case EvaluationQuestionLinkTypeEnum.Image:
        return <img width="100%" src={url} />
      case EvaluationQuestionLinkTypeEnum.Code:
        return <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(url) }} />
    }
  }

  const getStyles = (checked: boolean, isCorrect: boolean) => {
    switch (state) {
      case EvaluationStatesEnum.inProcess:
        return checked ? styles.checked : ''
      case EvaluationStatesEnum.onCheck:
        return checked ? (isCorrect ? styles.correct : styles.incorrect) : ''
      case EvaluationStatesEnum.onPreview:
        return checked ? (isCorrect ? styles.correct : styles.incorrect) : ''
      default:
        return ''
    }
  }

  return (
    <div className={styles.content}>
      {data.url && data.url.trim() && (
        <div className="mb-3">{prepareLink(data.url, data.urlType)}</div>
      )}
      <div className="mb-3">
        <div
          className={styles.questionTitle}
          dangerouslySetInnerHTML={{
            __html: sanitizeHtml(breakLinesFix(`${number}. ${data.name}`)),
          }}
        ></div>
      </div>
      <div className={styles.questionContainer}>
        {data.content.map(
          ({ likertId, id, label, checked }: IEvaluationQuestionOption) => (
            <CheckBoxRadioButtonInput
              className={`${styles.radioOption} ${getStyles(
                value && value.id === id,
                checked
              )}`}
              key={likertId + 'evaQuestionOption' + id}
              name={`${label}-${id}-${likertId}`}
              value={id}
              checked={value ? value.id === id : false}
              inline={false}
              type={''}
              label={label}
              onChange={() => onChange({ questionId: data.id, likertId, id })}
              disabled={!canEdit}
              error={''}
            />
          )
        )}
        {!canEdit && data.explanationAnswer && (
          <TextAreaInput
            readOnly={true}
            label={`${t(
              'administration.blocks.type.evaluation.explanationAnswer'
            )}`}
            rows={3}
            name={'ExplanationAnswer'}
            value={data.explanationAnswer}
          />
        )}
      </div>
    </div>
  )
}

export default EvaluationQuestion
